import { useDomTracking } from '@nrdy-marketing-engine/tracking';
import clsx from 'clsx';
import { ChangeEvent, ReactNode } from 'react';
import { InputFieldData, InputStateProps } from '../../@types/input-field-data';
import InputLabel from './input-label';
import InputValidation from './input-validation';

interface CheckboxInputProps extends InputStateProps, InputFieldData {
  /** Any extra classes to apply to the checkbox control. */
  checkboxClassName?: string;
  /** Any children that are passed to the InputLabel component */
  children?: ReactNode;
  /** Any extra classes to apply to the containing div. */
  className?: string;
}

/** A generic React-powered checkbox field. */
function CheckboxInput({
  autoFocus,
  checkboxClassName,
  children,
  className,
  name,
  onInput,
  required,
  text,
  type,
  validationErrors,
  value,
}: CheckboxInputProps) {
  const blurTrackable = useDomTracking('blur');
  const clickTrackable = useDomTracking('click');

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    /** Unify false and undefined for validation */
    if (required && !event.target.checked) return onInput(undefined);

    onInput(event.target.checked);
  }

  return (
    <div className={clsx('flex items-center mt-7', className)}>
      <InputLabel
        className="flex items-center"
        name={name}
        ref={clickTrackable}
        required={false}
        text={text}
        type="checkbox"
      >
        <input
          autoFocus={autoFocus}
          checked={value as boolean}
          className={clsx('form-checkbox h-5 w-5 text-gray-600 mr-2', checkboxClassName)}
          id={`${type}_input_${name || type}`}
          name={name}
          onChange={handleChange}
          ref={blurTrackable}
          role="checkbox"
          type="checkbox"
        />
        {children}
      </InputLabel>
      <InputValidation className="mt-0 ml-2" validationErrors={validationErrors} />
    </div>
  );
}

export default CheckboxInput;
